import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { fetchMyNetwork } from '@/api/network';

/** Minimal company info the rest of the app needs to render a company
 *  avatar / chip when all it has is the company id (e.g. a comment
 *  author, a notification, an audit log row). */
export interface NetworkCompanyInfo {
  id: string;
  name: string;
  featuredImageFileUrl: string | null;
}

/** Loads the user's network companies once (cached for 10 minutes) and
 *  exposes a `Map<companyId, NetworkCompanyInfo>` for O(1) lookups.
 *
 *  Mirrors the legacy `useNetworkCompaniesList` hook that the old
 *  responder used to resolve `getCommentImageUrl(commentedByCompanyId)`.
 *  Includes the user's own company so first-party comments also render
 *  the right avatar. */
export function useNetworkCompaniesById(opts?: { enabled?: boolean }): {
  byId: Map<string, NetworkCompanyInfo>;
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: ['network-companies', 'by-id'] as const,
    queryFn: () => fetchMyNetwork('isDropDown=true&includeOwnCompany=true'),
    staleTime: 10 * 60_000,
    enabled: opts?.enabled ?? true,
  });

  const byId = useMemo(() => {
    const map = new Map<string, NetworkCompanyInfo>();
    for (const c of query.data?.data ?? []) {
      map.set(c.id, {
        id: c.id,
        name: c.name,
        featuredImageFileUrl: c.featuredImageFileUrl,
      });
    }
    return map;
  }, [query.data]);

  return { byId, isLoading: query.isLoading };
}
